import { Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer as MuiTableContainer,
  TableRow,
  TextField,
  Typography,
  dialogClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AppDialog from "components/common/AppDialog";
import { AdvancedConfirmDialog, NoData, StyledTableHead, TableContainer } from "components";
import { LangConstant } from "const";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccountList, useChannelAddMember } from "hooks";

const MAX_ADD_MEMBER = 100;

const AddChannelMemberDialog = ({ open, groupId, onClose, onRefreshMemberList }) => {
  const { t: getLabel } = useTranslation();
  const labels = getLabel(LangConstant.OBJ_ADD_CHANNEL_MEMBER_DIALOG, { returnObjects: true });

  const tableContainerRef = useRef(null);

  const { accounts, isFetching, handleSearch, handleLoadMore, handleResetQuery } = useAccountList({ groupId });
  const { addChannelMembers, isLoading: isAdding } = useChannelAddMember();

  const [selectedIds, setSelectedIds] = useState([]);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);

  const columns = [
    { label: "", align: "center", width: "5%" },
    { label: labels.colFullName, align: "left" },
    { label: labels.colUnit, align: "left", width: "18%" },
    { label: labels.colDepartment, align: "left", width: "20%" },
    { label: labels.colPosition, align: "left", width: "15%" },
  ];

  // Load more when scrolled to bottom
  const handleScroll = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 8;
    if (isAtBottom) handleLoadMore();
  }, [handleLoadMore]);

  const handleSelectUser = useCallback(id => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  }, []);

  const handleConfirm = () => {
    addChannelMembers({
      groupId,
      memberIds: selectedIds,
      onSuccess: () => {
        setIsOpenConfirm(false);
        handleResetQuery();
        onRefreshMemberList();
        setSelectedIds([]);
      },
      onError: handleOpenConfirm(false),
    });
  };

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const handleOpenConfirm = isOpen => () => {
    setIsOpenConfirm(isOpen);
  };

  return (
    <StyledAppDialog open={open} title={labels.title} hideBackdrop onClose={handleClose}>
      <TextField
        fullWidth
        size="small"
        label={labels.searchLabel}
        placeholder={labels.searchPlaceholder}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        onChange={e => handleSearch(e.target.value)}
        sx={{ marginTop: 1 }}
      />
      <Typography style={{ fontSize: 16, fontWeight: 700 }}>
        {labels.selectedCount}:{" "}
        <Box component="span" fontWeight={400}>
          {selectedIds.length}
        </Box>
      </Typography>
      {/* User table */}
      <TableContainer ref={tableContainerRef} onScroll={handleScroll}>
        <Table stickyHeader>
          <StyledTableHead columns={columns} />
          <TableBody>
            {accounts.map(user => {
              const isChecked = selectedIds.includes(user.id);
              const isDisableCheckbox = !isChecked && selectedIds.length >= MAX_ADD_MEMBER;
              return (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => (isDisableCheckbox ? {} : handleSelectUser(user.id))}
                  sx={{ cursor: "pointer" }}
                  role="checkbox"
                >
                  <TableCell align="center" padding="checkbox">
                    <Checkbox disabled={isDisableCheckbox} checked={isChecked} />
                  </TableCell>
                  <TableCell align="left">{user.name}</TableCell>
                  <TableCell align="left">{user.unitName || "--"}</TableCell>
                  <TableCell align="left">{user.departmentName || "--"}</TableCell>
                  <TableCell align="left">{user.positionName || "--"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {accounts.length === 0 && !isFetching && <NoData />}
      </TableContainer>
      {/* Actions */}
      <Stack spacing={1}>
        <Button
          variant="contained"
          fullWidth
          disabled={selectedIds.length === 0 || isAdding}
          onClick={handleOpenConfirm(true)}
        >
          {labels.addMember}
        </Button>
        <Button variant="text" sx={{ color: "#000000" }} fullWidth onClick={handleClose}>
          {labels.cancel}
        </Button>
      </Stack>

      {isOpenConfirm && (
        <AdvancedConfirmDialog
          open
          title={getLabel("TXT_ADD_CHANNEL_MEMBER")}
          content={getLabel("TXT_ADD_CHANNEL_MEMBER_CONTENT")}
          submitProps={{ submitText: getLabel("L_CONFIRM_BUTTON"), onClick: handleConfirm }}
          cancelProps={{ onClick: handleOpenConfirm(false) }}
        />
      )}
    </StyledAppDialog>
  );
};

export default AddChannelMemberDialog;

const StyledAppDialog = styled(AppDialog)({
  [`& .${dialogClasses.paper}`]: {
    width: 1200,
    height: 615,
  },
});
